import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  TextField,
  Switch,
} from "@mui/material";
import HeaderReservationManagement from "../../layouts/HeaderReservationManagement";
import Navbar from "../../layouts/Navbar";
import TablePaginationLayout from "../../layouts/TablePagination";
import useOutsourcingStatementMaster from "../../features/outsourcingStatement";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import MultiSelectWithSearch from "../../components/MultiSelectWithSearch";
import { DateRangePicker } from "rsuite";
import { formatNumberWithUnit } from "../../utils";
import dayjs from "dayjs";

// Format 封入金額 with special handling for negative values
const formatFukinAmount = (amount) => {
  if (amount < 0) {
    return (
      <span>
        0 <span style={{ color: 'red' }}>({formatNumberWithUnit(amount)})</span>
      </span>
    );
  }
  return formatNumberWithUnit(amount);
};

const columnTitles = [
  "ID",
  "店舗",
  "担当者",
  "セラピスト報酬",
  "備品代",
  "備品代差し引き後の報酬",
  "封入金額", // Tính theo công thức Daily Report: total cost - therapist amount
];

const OutsourcingStatement = () => {
  const {
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    total,
    handleSort,
    params,
    setParms,
    areaOptions,
    therapistOptions,
    totalData,
    handleUpdateToggleIsGroupByTherapistAndDate,
  } = useOutsourcingStatementMaster();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
      <HeaderReservationManagement />
      <Navbar />
      <Container maxWidth="lg" sx={{}}>
        <Typography
          component="p"
          sx={{
            fontSize: {
              xs: 20,
              md: 28,
            },
            color: "#fff",
            top: "104px",
            fontFamily: "Roboto",
            fontStyle: "normal",
            fontWeight: "normal",
            lineHeight: "24px",
            letterSpacing: "3px",
            margin: "80px 0px 16px",
            textDecoration: "none",
            width: "100%",
            paddingBottom: "15px",
            borderBottom: "2px solid white",
            textAlign: "left",
          }}
        >
          業務委託清算書(現金)
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "30px",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <DatePicker
            label="日付"
            value={params.date ? dayjs(params.date) : null}
            onChange={(date) =>
              setParms((prev) => ({
                ...prev,
                date: date ? date.format("YYYY-MM-DD") : "",
              }))
            }
            renderInput={(params) => <TextField {...params} fullWidth />}
            sx={{ width: "200px" }}
          />
          <MultiSelectWithSearch
            options={areaOptions}
            values={params.area_names}
            onChange={(value) =>
              setParms((prev) => ({
                ...prev,
                area_names: value,
              }))
            }
            placeholderText="店舗"
            labelText="店舗"
            checkAllText="すべて選択"
            sx={{ width: "200px" }}
          />
          <MultiSelectWithSearch
            options={therapistOptions}
            values={params.therapist_ids}
            onChange={(value) =>
              setParms((prev) => ({
                ...prev,
                therapist_ids: value,
              }))
            }
            placeholderText="担当者"
            labelText="担当者"
            checkAllText="すべて選択"
            sx={{ width: "200px" }}
          />
          <DateRangePicker
            placement="autoVertical"
            className="my-custom-datepicker"
            format="yyyy/MM/dd"
            value={[
              params.start_date ? new Date(params.start_date) : null,
              params.end_date ? new Date(params.end_date) : null,
            ]}
            onChange={(value) => {
              setParms((prev) => ({
                ...prev,
                start_date:
                  value && value[0] ? dayjs(value[0]).format("YYYY-MM-DD") : "",
                end_date:
                  value && value[1] ? dayjs(value[1]).format("YYYY-MM-DD") : "",
              }));
            }}
            placeholder="期間を選択"
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Switch
              checked={!params.is_group_by_therapist_and_date}
              onChange={handleUpdateToggleIsGroupByTherapistAndDate}
            />
            <Typography>個別表示</Typography>
          </Box>
        </Box>
      </Container>
      <Container maxWidth="xl">
        <Box sx={{ marginTop: "40px" }}>
          <Paper sx={{ marginTop: "16px", marginBottom: "16px" }}>
            <TableContainer sx={{ maxHeight: "90vh" }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columnTitles.map((title, index) => {
                      const isSorted = activeSortColumn === index;
                      const sortDirection = isSorted
                        ? sortColumnDirection
                        : "asc";

                      return (
                        <TableCell
                          key={index}
                          align="center"
                          sx={{
                            top: 0,
                            backgroundColor: "#fafafa",
                            zIndex: 1,
                          }}
                          sortDirection={isSorted ? sortDirection : false}
                        >
                          <TableSortLabel
                            active={isSorted}
                            direction={sortDirection}
                            onClick={() => handleSort(index)}
                          >
                            {title}
                          </TableSortLabel>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row, rowIndex) => (
                    <TableRow key={rowIndex} className="MuiTableRow-hover">
                      <TableCell align="center">{row.id}</TableCell>
                      <TableCell align="center">{row.area}</TableCell>
                      <TableCell align="center">{row.therapist_name}</TableCell>
                      <TableCell align="center">
                        {formatNumberWithUnit(row.therapist_amount)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumberWithUnit(row.equipment_cost)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumberWithUnit(
                          row.therapist_amount_after_equipment
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {formatFukinAmount(row.sales_after_equipment)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {totalData && (
                    <TableRow
                      key={"totalData"}
                      className="MuiTableRow-hover"
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "#f0f0f0",
                        "& td": { fontWeight: "bold" },
                        zIndex: 1,
                      }}
                    >
                      <TableCell align="center"> </TableCell>
                      <TableCell align="center"> </TableCell>
                      <TableCell align="center">統計</TableCell>
                      <TableCell align="center">
                        {formatNumberWithUnit(totalData.therapist_amount)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumberWithUnit(totalData.equipment_cost)}
                      </TableCell>
                      <TableCell align="center">
                        {formatNumberWithUnit(
                          totalData.therapist_amount_after_equipment
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {formatFukinAmount(totalData.sales_after_equipment)}
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columnTitles.length} align="center">
                        <Typography>該当データがありません。</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePaginationLayout
              rowsPerPage={params.per_page}
              total={total}
              page={params.page - 1}
              rowsPerPageOptions={[50, 100, 125]}
              onPageChange={(_, newPage) => {
                setParms((prev) => ({
                  ...prev,
                  page: newPage + 1,
                }));
              }}
              handleChangeRowsPerPage={(_, newRowsPerPage) => {
                setParms((prev) => ({
                  ...prev,
                  page: 1,
                  per_page: newRowsPerPage,
                }));
              }}
            />
          </Paper>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default OutsourcingStatement;
